import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Search, Users, AlertCircle, RotateCcw, Package, Check } from 'lucide-react';
import { getResources, Resource } from '../../lib/resources';
import { getAssets, Asset } from '../../lib/assets';
import { useBusiness } from '../../contexts/BusinessContext';
import { getPersonInitials } from '../../utils/personInitials';

export type SelectResourceConfirmSelection = {
  resources: Resource[];
  assets: Asset[];
};

interface SelectResourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called when user confirms. Returns the full pending selection (empty = unassigned). */
  onConfirm: (selection: SelectResourceConfirmSelection) => void;
  /** Currently assigned resources (objects with `id`, or bare id strings). */
  selectedResources?: Array<{ id: string } | string>;
  selectedResourceId?: string | null;
  selectedAssetIds?: string[];
  assetsEnabled?: boolean;
  confirmLabel?: string;
}

function normalizeSelectedIds(
  selectedResources: Array<{ id: string } | string> = [],
  selectedResourceId?: string | null
): string[] {
  const ids = new Set<string>();
  for (const item of selectedResources) {
    if (typeof item === 'string' && item && item !== 'unassigned') {
      ids.add(item);
    } else if (item && typeof item === 'object' && item.id && item.id !== 'unassigned') {
      ids.add(item.id);
    }
  }
  if (selectedResourceId && selectedResourceId !== 'unassigned') {
    ids.add(selectedResourceId);
  }
  return Array.from(ids);
}

export default function SelectResourceModal({
  isOpen,
  onClose,
  onConfirm,
  selectedResources = [],
  selectedResourceId = null,
  selectedAssetIds = [],
  assetsEnabled = false,
  confirmLabel = 'Confirm',
}: SelectResourceModalProps) {
  const { selectedBusiness } = useBusiness();
  const [isAnimating, setIsAnimating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [resources, setResources] = useState<Resource[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'team' | 'assets'>('team');
  const [pendingResourceIds, setPendingResourceIds] = useState<Set<string>>(new Set());
  const [pendingAssetIds, setPendingAssetIds] = useState<Set<string>>(new Set());

  const loadResources = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setLoading(true);
      const data = await getResources(selectedBusiness.id);
      setResources(data.filter(resource => resource.status === 'active' && !resource.not_bookable));

      if (assetsEnabled) {
        const assetData = await getAssets(selectedBusiness.id);
        setAssets(assetData.filter(a => a.status === 'active' && !a.not_bookable));
      }

      setError(null);
    } catch (err) {
      console.error('Error loading resources:', err);
      setError(err instanceof Error ? err.message : 'Failed to load resources');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      setActiveTab('team');
      setSearchQuery('');
      setPendingResourceIds(new Set(normalizeSelectedIds(selectedResources, selectedResourceId)));
      setPendingAssetIds(new Set(selectedAssetIds.filter(Boolean)));
      loadResources();
    }
  }, [isOpen, selectedBusiness?.id]);

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
      setSearchQuery('');
      setPendingResourceIds(new Set());
      setPendingAssetIds(new Set());
    }
  };

  const filteredResources = resources.filter(resource => {
    const searchLower = searchQuery.toLowerCase();
    return (
      `${resource.first_name} ${resource.last_name}`.toLowerCase().includes(searchLower) ||
      resource.email?.toLowerCase().includes(searchLower)
    );
  });

  const filteredAssets = assets.filter(asset => {
    const searchLower = searchQuery.toLowerCase();
    return (
      asset.name.toLowerCase().includes(searchLower) ||
      (asset.asset_type || '').toLowerCase().includes(searchLower) ||
      (asset.description || '').toLowerCase().includes(searchLower)
    );
  });

  const pendingCount = pendingResourceIds.size + pendingAssetIds.size;

  const toggleResource = (id: string) => {
    setPendingResourceIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAsset = (id: string) => {
    setPendingAssetIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleConfirm = () => {
    const selectedResourceObjects = resources.filter(r => pendingResourceIds.has(r.id));
    const selectedAssetObjects = assets.filter(a => pendingAssetIds.has(a.id));
    onConfirm({ resources: selectedResourceObjects, assets: selectedAssetObjects });
    onClose();
  };

  if (!isOpen && !isAnimating) return null;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  const getAssetInitials = (name: string) => {
    return name.split(/\s+/).map(w => w[0]).join('').toUpperCase().slice(0, 2);
  };

  const modalContent = (
    <div 
      className={`fixed inset-0 transition-opacity duration-200 ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      }`}
      onClick={onClose}
      onTransitionEnd={handleTransitionEnd}
      style={{
        zIndex: 100200,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        paddingTop: isMobile ? 'env(safe-area-inset-top, 0px)' : '0',
        paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
      }}
    >
      <div 
        className={`bg-white transform transition-all duration-200 ${
          isMobile 
            ? `relative w-full h-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[600px] shadow-2xl ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{ 
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          zIndex: 100210
        }}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden">
          {/* Close Button */}
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X className="h-5 w-5" />
          </button>

          {/* Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center relative">
            <h2 className="text-2xl font-bold text-gray-900" style={{ 
              letterSpacing: '-0.02em'
            }}>
              {assetsEnabled && activeTab === 'assets' ? 'Assign Assets' : 'Assign Resources'}
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Tap to select or unselect, then confirm
            </p>
          </div>

          {/* Tabs - only show when assets are enabled */}
          {assetsEnabled && (
            <div className="bg-white border-b border-gray-200 px-6 py-3">
              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  type="button"
                  onClick={() => { setActiveTab('team'); setSearchQuery(''); }}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                    activeTab === 'team'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Users className="h-4 w-4" />
                  Team
                  {pendingResourceIds.size > 0 && (
                    <span className="ml-0.5 text-xs font-semibold text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded-full">
                      {pendingResourceIds.size}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => { setActiveTab('assets'); setSearchQuery(''); }}
                  className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                    activeTab === 'assets'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Package className="h-4 w-4" />
                  Assets
                  {pendingAssetIds.size > 0 && (
                    <span className="ml-0.5 text-xs font-semibold text-orange-600 bg-orange-100 px-1.5 py-0.5 rounded-full">
                      {pendingAssetIds.size}
                    </span>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* Search Section */}
          <div className="bg-white border-b border-gray-200 px-6 py-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder={activeTab === 'assets' ? 'Search assets...' : 'Search team members...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-150"
                style={{ fontSize: '16px' }}
              />
            </div>
          </div>

          {/* Content */}
          <div 
            className="flex-1 overflow-y-auto bg-white"
            style={{ 
              WebkitOverflowScrolling: 'touch',
              paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : '0'
            }}
          >
            <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-4`} style={{
              paddingBottom: isMobile ? 'calc(1rem + env(safe-area-inset-bottom, 0px))' : '1rem'
            }}>

              {activeTab === 'team' ? (
                <>
                  {loading ? (
                    <div className="flex items-center justify-center h-64">
                      <div className="text-center">
                        <div className="w-8 h-8 border-2 border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
                        <p className="mt-4 text-sm text-gray-600">Loading team members...</p>
                      </div>
                    </div>
                  ) : error ? (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                      <div className="flex items-start">
                        <AlertCircle className="h-5 w-5 text-red-500 mr-2 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-red-700 mb-1">
                            Error loading resources
                          </p>
                          <p className="text-sm text-red-600 mb-3">
                            {error}
                          </p>
                          <button
                            type="button"
                            onClick={loadResources}
                            className="inline-flex items-center px-3 py-2 text-xs font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                          >
                            <RotateCcw className="h-3 w-3 mr-1" />
                            Try again
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : filteredResources.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                        <Users className="h-8 w-8 text-gray-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {searchQuery ? 'No matching team members' : 'No team members available'}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {searchQuery ? 'Try adjusting your search terms' : 'Add team members to your business to assign resources'}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredResources.map((resource) => {
                        const isSelected = pendingResourceIds.has(resource.id);

                        return (
                          <button
                            type="button"
                            key={resource.id}
                            onClick={() => toggleResource(resource.id)}
                            className={`w-full flex items-center p-3 rounded-xl border transition-all duration-150 active:scale-[0.98] touch-manipulation ${
                              isSelected
                                ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                                : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50/50'
                            }`}
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >
                            <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center text-sm font-semibold text-white overflow-hidden">
                              {resource.avatar_url ? (
                                <img 
                                  src={resource.avatar_url} 
                                  alt={`${resource.first_name} ${resource.last_name}`}
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                <>
                                  {getPersonInitials(resource.first_name, resource.last_name)}
                                </>
                              )}
                            </div>
                            <div className="ml-3 text-left flex-1">
                              <div className="text-sm font-semibold text-gray-900">
                                {resource.first_name} {resource.last_name}
                              </div>
                              <div className="text-xs text-gray-600">
                                {resource.email}
                              </div>
                            </div>
                            <div
                              className={`h-6 w-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                                isSelected
                                  ? 'border-blue-500 bg-blue-500 text-white'
                                  : 'border-gray-300 bg-white'
                              }`}
                            >
                              {isSelected && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              ) : (
                /* Assets tab */
                <>
                  {loading ? (
                    <div className="flex items-center justify-center h-64">
                      <div className="text-center">
                        <div className="w-8 h-8 border-2 border-gray-200 border-t-orange-600 rounded-full animate-spin mx-auto"></div>
                        <p className="mt-4 text-sm text-gray-600">Loading assets...</p>
                      </div>
                    </div>
                  ) : filteredAssets.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-orange-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                        <Package className="h-8 w-8 text-orange-400" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2">
                        {searchQuery ? 'No matching assets' : 'No assets available'}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {searchQuery ? 'Try adjusting your search terms' : 'Add assets to your business first'}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredAssets.map((asset) => {
                        const isSelected = pendingAssetIds.has(asset.id);
                        const initials = getAssetInitials(asset.name);

                        return (
                          <button
                            type="button"
                            key={asset.id}
                            onClick={() => toggleAsset(asset.id)}
                            className={`w-full flex items-center p-3 rounded-xl border transition-all duration-150 active:scale-[0.98] touch-manipulation ${
                              isSelected
                                ? 'border-orange-500 bg-orange-50 ring-1 ring-orange-500'
                                : 'border-gray-200 hover:border-orange-300 hover:bg-orange-50/50'
                            }`}
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >
                            <div className="h-10 w-10 rounded-full bg-orange-500 flex items-center justify-center text-sm font-semibold text-white overflow-hidden">
                              {asset.image_url ? (
                                <img src={asset.image_url} alt={asset.name} className="h-full w-full object-cover" />
                              ) : (
                                initials
                              )}
                            </div>
                            <div className="ml-3 text-left flex-1">
                              <div className="text-sm font-semibold text-gray-900">
                                {asset.name}
                              </div>
                              <div className="text-xs text-gray-600">
                                {asset.asset_type ? asset.asset_type.charAt(0).toUpperCase() + asset.asset_type.slice(1) : 'Asset'}
                                {asset.description ? ` · ${asset.description}` : ''}
                              </div>
                            </div>
                            <div
                              className={`h-6 w-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                                isSelected
                                  ? 'border-orange-500 bg-orange-500 text-white'
                                  : 'border-gray-300 bg-white'
                              }`}
                            >
                              {isSelected && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Confirm footer */}
          <div
            className="border-t border-gray-200 bg-white px-6 py-4"
            style={{
              paddingBottom: isMobile
                ? 'calc(1rem + env(safe-area-inset-bottom, 0px))'
                : '1rem',
            }}
          >
            <button
              type="button"
              onClick={handleConfirm}
              className="w-full py-3.5 px-4 rounded-xl bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white text-sm font-semibold transition-colors touch-manipulation"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              {pendingCount === 0
                ? `${confirmLabel} (none selected)`
                : `${confirmLabel}${pendingCount > 0 ? ` (${pendingCount})` : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
